package com.example.positionahead.domain

import com.example.positionahead.model.AssetClass
import com.example.positionahead.model.FinancialGoal
import com.example.positionahead.model.PortfolioObjective
import com.example.positionahead.model.PortfolioSummary
import com.example.positionahead.model.PositionSnapshot
import com.example.positionahead.model.Quote
import com.example.positionahead.model.Recommendation
import com.example.positionahead.model.RecommendationAction
import com.example.positionahead.model.RiskTolerance
import com.example.positionahead.model.effectiveBondTarget
import com.example.positionahead.model.effectiveStockTarget
import com.example.positionahead.model.objectiveLabel
import kotlin.math.abs

object PortfolioAnalyzer {

    private const val DISCLAIMER =
        " Educational guidance only — not professional financial advice."

    fun analyze(
        summary: PortfolioSummary,
        goal: FinancialGoal,
        marketQuotes: Map<String, Quote> = emptyMap(),
    ): List<Recommendation> {
        if (summary.positions.isEmpty()) {
            return suggestInitialIdeas(goal, marketQuotes)
        }

        val recommendations = mutableListOf<Recommendation>()
        recommendations += analyzeObjectiveFit(summary, goal, marketQuotes)
        recommendations += analyzeAssetAllocation(summary, goal, marketQuotes)
        recommendations += analyzeConcentration(summary, goal, marketQuotes)
        recommendations += analyzeUnderperformers(summary, goal, marketQuotes)
        recommendations += analyzeTakeProfits(summary, goal, marketQuotes)
        recommendations += analyzeUnownedOpportunities(summary, goal, marketQuotes)
        recommendations += analyzeDiversificationGaps(summary, goal, marketQuotes)
        recommendations += analyzeIncome(summary, goal)
        recommendations += actionableRebalanceTrades(summary, goal, marketQuotes)

        return recommendations
            .sortedBy { it.priority }
            .distinctBy { it.title }
            .take(12)
    }

    private fun actionableRebalanceTrades(
        summary: PortfolioSummary,
        goal: FinancialGoal,
        marketQuotes: Map<String, Quote>,
    ): List<Recommendation> {
        val trades = RebalancePlanner.computeTrades(summary, goal, marketQuotes)
        if (trades.isEmpty()) return emptyList()

        return trades.mapIndexed { index, trade ->
            val sell = trade.sellSymbol
            val buy = trade.buySymbol
            val sellAmt = trade.sellAmountDollars
            val buyAmt = trade.buyAmountDollars
            val title = when {
                sell != null && buy != null ->
                    "Sell ${formatCurrency(sellAmt)} of $sell, buy ${formatCurrency(buyAmt)} of $buy"
                sell != null -> "Sell ${formatCurrency(sellAmt)} of $sell"
                buy != null -> "Buy ${formatCurrency(buyAmt)} of $buy"
                else -> "Rebalance portfolio"
            }
            Recommendation(
                action = RecommendationAction.REBALANCE,
                title = title,
                detail = "${trade.reason}. Target mix: ${goal.effectiveStockTarget()}% stocks / " +
                    "${goal.effectiveBondTarget()}% bonds.$DISCLAIMER",
                priority = 0,
                sellSymbol = sell,
                sellAmountDollars = sellAmt,
                buySymbol = buy,
                buyAmountDollars = buyAmt,
            )
        }
    }

    private fun suggestInitialIdeas(goal: FinancialGoal, marketQuotes: Map<String, Quote>): List<Recommendation> {
        val candidates = MarketSuggestionCatalog.candidateSymbolsFor(goal).take(3)
        if (candidates.isEmpty()) {
            return listOf(
                Recommendation(
                    action = RecommendationAction.BUY,
                    title = "Start tracking your portfolio",
                    detail = "Add positions to see market value, gains, and personalized ideas.$DISCLAIMER",
                    priority = 1,
                ),
            )
        }

        return candidates.mapIndexed { index, symbol ->
            considerBuying(
                symbol = symbol,
                reason = "Starting a ${goal.objectiveLabel()} portfolio — $symbol may fit your goals",
                marketQuotes = marketQuotes,
                priority = index + 1,
            )
        }
    }

    private fun analyzeObjectiveFit(
        summary: PortfolioSummary,
        goal: FinancialGoal,
        marketQuotes: Map<String, Quote>,
    ): List<Recommendation> {
        val yieldPercent = if (summary.totalMarketValue > 0) {
            (summary.estimatedAnnualIncome / summary.totalMarketValue) * 100
        } else 0.0
        val owned = ownedSymbols(summary)

        return when (goal.objective) {
            PortfolioObjective.GROWTH -> buildList {
                if (summary.stockAllocationPercent < goal.effectiveStockTarget() - goal.rebalanceThresholdPercent) {
                    addAll(
                        considerBuyingList(
                            symbols = MarketSuggestionCatalog.growthEtfs.filter { it !in owned }.take(2),
                            reason = "Growth plan targets ${goal.effectiveStockTarget()}% stocks; you're at " +
                                "${summary.stockAllocationPercent.format(1)}% — adding equities may help returns",
                            marketQuotes = marketQuotes,
                            priority = 1,
                        ),
                    )
                }
                if (yieldPercent > goal.targetYieldPercent + 1.5) {
                    val incomeHeavy = summary.positions
                        .filter { (it.quote?.dividendYield ?: 0.0) >= 5.0 }
                        .sortedByDescending { it.portfolioWeightPercent }
                        .take(2)
                    if (incomeHeavy.isNotEmpty()) {
                        addAll(
                            incomeHeavy.map { snapshot ->
                                considerTrimming(
                                    snapshot = snapshot,
                                    reason = "Growth objective — portfolio yield is ${yieldPercent.format(2)}%, " +
                                        "above your ${goal.targetYieldPercent.format(1)}% target; trimming income holdings may free capital for growth",
                                    marketQuotes = marketQuotes,
                                    priority = 2,
                                )
                            },
                        )
                    } else {
                        add(
                            Recommendation(
                                action = RecommendationAction.REBALANCE,
                                title = "Consider rebalancing for growth",
                                detail = "Portfolio yield is ${yieldPercent.format(2)}%. Shifting some income holdings toward growth names like VTI or QQQM may improve long-term returns.$DISCLAIMER",
                                priority = 2,
                            ),
                        )
                    }
                }
            }
            PortfolioObjective.INCOME -> buildList {
                if (yieldPercent + 0.25 < goal.targetYieldPercent) {
                    val buySymbols = MarketSuggestionCatalog.incomeEtfs
                        .filter { it !in owned }
                        .sortedByDescending { marketQuotes[it.uppercase()]?.dividendYield ?: 0.0 }
                        .take(2)
                    addAll(
                        considerBuyingList(
                            symbols = buySymbols.ifEmpty { MarketSuggestionCatalog.incomeEtfs.take(2) },
                            reason = "Income plan targets ${goal.targetYieldPercent.format(1)}% yield; " +
                                "portfolio is at ${yieldPercent.format(2)}% — higher-yield holdings may boost cash flow",
                            marketQuotes = marketQuotes,
                            priority = 1,
                        ),
                    )
                } else if (yieldPercent >= goal.targetYieldPercent) {
                    add(
                        Recommendation(
                            action = RecommendationAction.HOLD,
                            title = "Income objective on track",
                            detail = "Estimated yield ${yieldPercent.format(2)}% meets your ${goal.targetYieldPercent.format(1)}% income target.",
                            priority = 3,
                        ),
                    )
                }
            }
            PortfolioObjective.CAPITAL_PRESERVATION -> buildList {
                if (summary.bondAllocationPercent + summary.cashAllocationPercent <
                    goal.effectiveBondTarget() - goal.rebalanceThresholdPercent
                ) {
                    addAll(
                        considerBuyingList(
                            symbols = MarketSuggestionCatalog.preservationEtfs.filter { it !in owned }.take(2),
                            reason = "Preservation plan targets ${goal.effectiveBondTarget()}% bonds/cash; " +
                                "you're at ${(summary.bondAllocationPercent + summary.cashAllocationPercent).format(1)}%",
                            marketQuotes = marketQuotes,
                            priority = 1,
                        ),
                    )
                }
            }
            PortfolioObjective.BALANCED -> emptyList()
        }
    }

    private fun analyzeAssetAllocation(
        summary: PortfolioSummary,
        goal: FinancialGoal,
        marketQuotes: Map<String, Quote>,
    ): List<Recommendation> {
        val recommendations = mutableListOf<Recommendation>()
        val targetStock = goal.effectiveStockTarget().toDouble()
        val targetBond = goal.effectiveBondTarget().toDouble()
        val stockDrift = summary.stockAllocationPercent - targetStock
        val bondDrift = summary.bondAllocationPercent - targetBond
        val owned = ownedSymbols(summary)

        when {
            stockDrift > goal.rebalanceThresholdPercent -> {
                recommendations += summary.positions
                    .filter { isEquityLike(it) }
                    .sortedByDescending { it.portfolioWeightPercent }
                    .take(2)
                    .map { snapshot ->
                        considerTrimming(
                            snapshot = snapshot,
                            reason = "Stocks are ${summary.stockAllocationPercent.format(1)}% vs ${targetStock.format(0)}% target " +
                                "(${goal.objectiveLabel()}, age ${goal.age}) — trimming may reduce risk",
                            marketQuotes = marketQuotes,
                            priority = 1,
                        )
                    }
            }
            stockDrift < -goal.rebalanceThresholdPercent -> {
                recommendations += considerBuyingList(
                    symbols = MarketSuggestionCatalog.candidateSymbolsFor(goal.objective)
                        .filter { it !in owned }
                        .take(2),
                    reason = "Stocks are ${summary.stockAllocationPercent.format(1)}% vs ${targetStock.format(0)}% target — adding equities may improve growth potential",
                    marketQuotes = marketQuotes,
                    priority = 1,
                )
            }
            abs(bondDrift) > goal.rebalanceThresholdPercent -> {
                if (bondDrift > 0) {
                    recommendations += summary.positions
                        .filter { it.position.assetClass == AssetClass.BOND }
                        .sortedByDescending { it.portfolioWeightPercent }
                        .take(1)
                        .map { snapshot ->
                            considerTrimming(
                                snapshot = snapshot,
                                reason = "Bonds are ${summary.bondAllocationPercent.format(1)}% vs ${targetBond.format(0)}% target",
                                marketQuotes = marketQuotes,
                                priority = 2,
                            )
                        }
                } else {
                    recommendations += considerBuyingList(
                        symbols = listOf("BND").filter { it !in owned },
                        reason = "Bonds are ${summary.bondAllocationPercent.format(1)}% vs ${targetBond.format(0)}% target — fixed income may add stability",
                        marketQuotes = marketQuotes,
                        priority = 2,
                    )
                }
            }
            else -> {
                recommendations += Recommendation(
                    action = RecommendationAction.HOLD,
                    title = "Asset mix is on target",
                    detail = "Stock/bond split is within ${goal.rebalanceThresholdPercent.format(0)}% of your " +
                        "${goal.objectiveLabel()} allocation.",
                    priority = 3,
                )
            }
        }
        return recommendations
    }

    private fun analyzeConcentration(
        summary: PortfolioSummary,
        goal: FinancialGoal,
        marketQuotes: Map<String, Quote>,
    ): List<Recommendation> =
        summary.positions
            .filter { it.portfolioWeightPercent > goal.maxSinglePositionPercent }
            .map { snapshot ->
                considerTrimming(
                    snapshot = snapshot,
                    reason = "${snapshot.position.symbol} is ${snapshot.portfolioWeightPercent.format(1)}% of the portfolio — " +
                        "spreading capital across more holdings may reduce concentration risk",
                    marketQuotes = marketQuotes,
                    priority = 1,
                )
            }

    private fun analyzeUnderperformers(
        summary: PortfolioSummary,
        goal: FinancialGoal,
        marketQuotes: Map<String, Quote>,
    ): List<Recommendation> {
        if (goal.riskTolerance == RiskTolerance.AGGRESSIVE) return emptyList()

        return summary.positions
            .filter { it.hasLivePrice && it.unrealizedGainLossPercent < -15 }
            .map { snapshot ->
                val action = if (goal.riskTolerance == RiskTolerance.CONSERVATIVE) {
                    RecommendationAction.SELL
                } else {
                    RecommendationAction.HOLD
                }
                Recommendation(
                    action = action,
                    title = if (action == RecommendationAction.SELL) {
                        "Consider reviewing ${snapshot.position.symbol}"
                    } else {
                        "Review ${snapshot.position.symbol}"
                    },
                    detail = buildString {
                        append("Down ${snapshot.unrealizedGainLossPercent.format(1)}% vs purchase")
                        appendPositionMarketContext(snapshot, marketQuotes)
                        append(
                            if (action == RecommendationAction.SELL) {
                                ". You may want to trim or exit and redeploy into stronger opportunities."
                            } else {
                                ". Reassess before adding more."
                            },
                        )
                        append(DISCLAIMER)
                    },
                    priority = 2,
                )
            }
    }

    private fun analyzeTakeProfits(
        summary: PortfolioSummary,
        goal: FinancialGoal,
        marketQuotes: Map<String, Quote>,
    ): List<Recommendation> =
        summary.positions
            .filter { it.hasLivePrice && it.unrealizedGainLossPercent >= 20 }
            .sortedByDescending { it.unrealizedGainLossPercent }
            .take(2)
            .map { snapshot ->
                Recommendation(
                    action = RecommendationAction.REBALANCE,
                    title = "Consider taking profits on ${snapshot.position.symbol}",
                    detail = buildString {
                        append("Up ${snapshot.unrealizedGainLossPercent.format(1)}% vs purchase")
                        appendPositionMarketContext(snapshot, marketQuotes)
                        append(". Locking in some gains and redeploying may help maximize long-term returns.")
                        append(DISCLAIMER)
                    },
                    priority = 2,
                )
            }

    private fun analyzeUnownedOpportunities(
        summary: PortfolioSummary,
        goal: FinancialGoal,
        marketQuotes: Map<String, Quote>,
    ): List<Recommendation> {
        val owned = ownedSymbols(summary)
        val unowned = MarketSuggestionCatalog.candidatesFor(goal)
            .filter { it.symbol.uppercase() !in owned }
            .mapNotNull { entry -> marketQuotes[entry.symbol.uppercase()]?.let { entry.symbol.uppercase() to it } }

        if (unowned.isEmpty()) return emptyList()

        val ranked = when (goal.objective) {
            PortfolioObjective.GROWTH -> unowned.sortedByDescending { (_, quote) -> quote.changePercent }
            PortfolioObjective.INCOME -> unowned.sortedByDescending { (_, quote) -> quote.dividendYield }
            PortfolioObjective.CAPITAL_PRESERVATION -> unowned.sortedBy { (_, quote) -> abs(quote.changePercent) }
            PortfolioObjective.BALANCED -> unowned.sortedByDescending { (_, quote) ->
                quote.dividendYield + quote.changePercent
            }
        }.take(3)

        return ranked.map { (symbol, quote) ->
            val rationale = when (goal.objective) {
                PortfolioObjective.GROWTH ->
                    "Not in your portfolio — recent momentum and broad exposure may support growth"
                PortfolioObjective.INCOME ->
                    "Not in your portfolio — higher dividend yield may boost income"
                PortfolioObjective.CAPITAL_PRESERVATION ->
                    "Not in your portfolio — may add stability to your mix"
                PortfolioObjective.BALANCED ->
                    "Not in your portfolio — may improve diversification and risk-adjusted returns"
            }
            considerBuying(
                symbol = symbol,
                reason = rationale,
                marketQuotes = marketQuotes,
                priority = 3,
                quoteOverride = quote,
            )
        }
    }

    private fun analyzeDiversificationGaps(
        summary: PortfolioSummary,
        goal: FinancialGoal,
        marketQuotes: Map<String, Quote>,
    ): List<Recommendation> {
        val owned = ownedSymbols(summary)
        val gaps = mutableListOf<Recommendation>()

        if ("VXUS" !in owned && goal.objective in setOf(
                PortfolioObjective.GROWTH,
                PortfolioObjective.BALANCED,
            )
        ) {
            gaps += considerBuying(
                symbol = "VXUS",
                reason = "No international exposure detected — adding global equities may reduce US-only risk",
                marketQuotes = marketQuotes,
                priority = 4,
            )
        }

        if ("BND" !in owned && (
                goal.objective == PortfolioObjective.CAPITAL_PRESERVATION ||
                    goal.objective == PortfolioObjective.BALANCED ||
                    goal.age >= 55
                )
        ) {
            gaps += considerBuying(
                symbol = "BND",
                reason = "No bond holdings detected — fixed income may cushion volatility as you maximize steady returns",
                marketQuotes = marketQuotes,
                priority = 4,
            )
        }

        if ("SCHD" !in owned && goal.objective == PortfolioObjective.INCOME) {
            gaps += considerBuying(
                symbol = "SCHD",
                reason = "No dividend-focused ETF detected — may complement your income goal",
                marketQuotes = marketQuotes,
                priority = 4,
            )
        }

        return gaps
    }

    private fun analyzeIncome(summary: PortfolioSummary, goal: FinancialGoal): List<Recommendation> {
        if (summary.estimatedAnnualIncome <= 0 || summary.totalMarketValue <= 0) return emptyList()
        val yieldPercent = (summary.estimatedAnnualIncome / summary.totalMarketValue) * 100
        return listOf(
            Recommendation(
                action = RecommendationAction.HOLD,
                title = "Estimated portfolio yield",
                detail = "Projected ${yieldPercent.format(2)}% annual income " +
                    "(${summary.estimatedAnnualIncome.format(0)} total) over ${goal.investmentHorizonYears}-yr horizon.",
                priority = 5,
            ),
        )
    }

    private fun considerBuyingList(
        symbols: List<String>,
        reason: String,
        marketQuotes: Map<String, Quote>,
        priority: Int,
    ): List<Recommendation> =
        symbols.map { symbol ->
            considerBuying(symbol, reason, marketQuotes, priority)
        }

    private fun considerBuying(
        symbol: String,
        reason: String,
        marketQuotes: Map<String, Quote>,
        priority: Int,
        quoteOverride: Quote? = null,
    ): Recommendation = Recommendation(
        action = RecommendationAction.BUY,
        title = "Consider $symbol",
        detail = buildString {
            append(reason)
            val quote = quoteOverride ?: marketQuotes[symbol.uppercase()]
            quote?.let {
                append(" · ${formatQuote(it)}")
                if (it.dividendYield > 0) append(" · ${it.dividendYield.format(2)}% yield")
            }
            append(DISCLAIMER)
        },
        priority = priority,
    )

    private fun considerTrimming(
        snapshot: PositionSnapshot,
        reason: String,
        marketQuotes: Map<String, Quote>,
        priority: Int,
    ): Recommendation = Recommendation(
        action = RecommendationAction.SELL,
        title = "Consider trimming ${snapshot.position.symbol}",
        detail = buildString {
            append(reason)
            appendPositionMarketContext(snapshot, marketQuotes)
            append(DISCLAIMER)
        },
        priority = priority,
    )

    private fun ownedSymbols(summary: PortfolioSummary): Set<String> =
        summary.positions.map { it.position.symbol.uppercase() }.toSet()

    private fun StringBuilder.appendPositionMarketContext(
        snapshot: PositionSnapshot,
        marketQuotes: Map<String, Quote>,
    ) {
        val quote = snapshot.quote ?: marketQuotes[snapshot.position.symbol.uppercase()]
        quote?.let { append(" · ${formatQuote(it)}") }
        if (snapshot.hasLivePrice) {
            append(" · ${formatCurrency(snapshot.unrealizedGainLoss)} (${snapshot.unrealizedGainLossPercent.format(1)}% vs purchase)")
        }
    }

    private fun formatQuote(quote: Quote): String {
        val change = quote.changePercent
        val changeLabel = if (change >= 0) "+${change.format(2)}%" else "${change.format(2)}%"
        val name = quote.name.takeIf { it.isNotBlank() }?.let { " · $it" }.orEmpty()
        return "${quote.symbol} ${formatCurrency(quote.price)} ($changeLabel today)$name"
    }

    private fun formatCurrency(amount: Double): String {
        val sign = if (amount < 0) "-" else ""
        val absAmount = kotlin.math.abs(amount)
        val rounded = kotlin.math.round(absAmount * 100) / 100
        return "$sign$$rounded"
    }

    private fun isEquityLike(snapshot: PositionSnapshot): Boolean =
        when (snapshot.position.assetClass) {
            AssetClass.STOCK, AssetClass.ETF, AssetClass.REIT -> true
            AssetClass.BOND, AssetClass.CASH -> false
        }

    private fun Double.format(decimals: Int): String {
        val factor = 10.0.pow(decimals)
        val rounded = kotlin.math.round(this * factor) / factor
        return if (decimals == 0) rounded.toInt().toString() else rounded.toString()
    }

    private fun Double.pow(n: Int): Double {
        var result = 1.0
        repeat(n) { result *= 10.0 }
        return result
    }
}
